import { inject, Injectable } from "@angular/core";
import { ChangeUserInfoNavigationService } from "@modules/shared/modals/change-personal-info/domain/infrastructure/change-user-info-navigation.service";
import {
	RequestApiFrom,
	userAction
} from "@modules/shared/modals/change-personal-info/domain/entities/change-user-info.model";
import { UserService } from "@main-services/user.service";
import { BehaviorSubject, combineLatest, Observable } from "rxjs";
import { ChangeEmailStep } from "@modules/shared/modals/change-personal-info/constants/user-info-constants.model";
import { ChangeUserInfoRemote } from "@modules/shared/modals/change-personal-info/domain/infrastructure/change-user-info.remote";
import { validateEmail } from "@modules/shared/modals/change-personal-info/domain/util/change-personal-info.util";
import { take } from "rxjs/operators";
import { Helper } from "@helpers/functions";
import { ToastrService } from "ngx-toastr";
import { TranslationService } from "@core-services/translation.service";
import { NewProfileFacade } from "@modules/main/components/profile/new-profile/state/new-profile.facade";
import { SegmentService } from "@main-services/segment.service";
import {
	CHANGE_EMAIL_BUTTON,
	CHANGE_EMAIL_CONFIRMATION_EMAIL,
	CHANGE_EMAIL_SUCCESS,
	ChangeInfoTrackingModel
} from "@modules/shared/modals/change-personal-info/constants/change-info-tracking.model";
import { KycService } from "@modules/kyc/services/kyc.service";
import { UniversalModalService } from "@modules/+new/shared/universal-modal/universal-modal.service";
import { ModalCloseReason } from "@modules/+new/shared/universal-modal/entities/modal-close-reason.enum";
import { RudderstackService } from "@main-services/rudderstack.service";

interface ChangeEmailData {
	email: string;
	step: ChangeEmailStep;
	isButtonOn: boolean;
	inputError: boolean;
	errorSmsPin: boolean;
}
export interface ChangeEmailVM {
	changeEmailData: ChangeEmailData;
	is2faActive: boolean;
}

const initialChangeEmailData: ChangeEmailData = {
	email: "",
	step: ChangeEmailStep.UpdateData,
	isButtonOn: false,
	inputError: false,
	errorSmsPin: false
};
@Injectable({
	providedIn: "root"
})
export class ChangeUserEmailFacade {
	private readonly navigationService = inject(ChangeUserInfoNavigationService);
	private readonly changeUserInfoRemote = inject(ChangeUserInfoRemote);
	private readonly translationService = inject(TranslationService);
	private readonly universalModal = inject(UniversalModalService);
	private readonly newProfileFacade = inject(NewProfileFacade);
	private readonly segmentService = inject(SegmentService);
	private readonly rudderstackService = inject(RudderstackService);
	private readonly userService = inject(UserService);
	private readonly kycService = inject(KycService);
	private readonly toastr = inject(ToastrService);

	public readonly isKycForce$: Observable<boolean> = this.kycService.shouldForceKycFlow$();

	private changeEmailData$$ = new BehaviorSubject<ChangeEmailData>(initialChangeEmailData);
	public readonly changeEmailVM$: Observable<ChangeEmailVM> = combineLatest({
		changeEmailData: this.changeEmailData$$.asObservable(),
		is2faActive: this.userService.has2fa$
	});

	public onUserTypeEmail(email: string): void {
		this.onUserInput(email, userAction.OnUserType);
	}

	public onUserFocusOut(email: string): void {
		this.onUserInput(email, userAction.OnFocusOut);
	}

	private onUserInput(email: string, userInput: userAction): void {
		this.changeEmailData$$.next({
			...this.changeEmailData$$.value,
			email: email,
			inputError: userInput === userAction.OnUserType ? false : email !== "" && !validateEmail(email),
			isButtonOn: email !== "" && validateEmail(email)
		});
	}

	public onSaveChangesEmail(is2faActive: boolean): void {
		this.rudderstackTracker(CHANGE_EMAIL_BUTTON);
		this.changeEmailData$$.next({ ...this.changeEmailData$$.value, step: ChangeEmailStep.VerificationCode });
		!is2faActive ? this.requestSmsCodeVerification() : null;
	}

	public requestSmsCodeVerification(): void {
		this.changeUserInfoRemote
			.requestSmsCodeNewEmail$()
			.pipe(take(1))
			.subscribe({
				error: (error) => {
					if (error.responseErrors[0].code === "sms.verification.required") {
						return;
					}
					this.onApiError(error, RequestApiFrom.RequestSmsCode);
				}
			});
	}

	public onCodeVerification(event: string, email: string, is2faActive: boolean): void {
		this.changeUserInfoRemote
			.postNewEmail$(event, email, is2faActive)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.onApiNext(RequestApiFrom.PostNewEmail);
				},
				error: (error) => {
					this.onApiError(error, RequestApiFrom.PostNewEmail);
				}
			});
	}

	public onConfirmationEmail(code: string): void {
		this.changeUserInfoRemote
			.putNewEmailConfirmation$(code)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.onApiNext(RequestApiFrom.ConfirmEmail);
				},
				error: (error) => {
					this.onApiError(error, RequestApiFrom.ConfirmEmail);
				}
			});
	}

	private onApiNext(requestFrom: RequestApiFrom): void {
		switch (requestFrom) {
			case RequestApiFrom.PostNewEmail:
				this.changeEmailData$$.next({
					...this.changeEmailData$$.value,
					step: ChangeEmailStep.EmailConfirmation
				});
				this.rudderstackTracker(CHANGE_EMAIL_CONFIRMATION_EMAIL);
				break;
			case RequestApiFrom.ConfirmEmail:
				this.newProfileFacade.fetchProfile();
				this.toastr.success(this.translationService.translate("email_updated_successfully"));
				this.rudderstackTracker(CHANGE_EMAIL_SUCCESS);
				this.closeModal();
				this.resetBehaviorSubject();
				break;
			default:
				break;
		}
	}

	private onApiError(error, requestFrom: RequestApiFrom): void {
		requestFrom === RequestApiFrom.PostNewEmail
			? this.changeEmailData$$.next({ ...this.changeEmailData$$.value, errorSmsPin: true })
			: null;
		Helper.handleError(error);
	}

	public navigateToMyAccount(): void {
		this.navigationService.navigateToMyAccount();
	}

	public goToKyc(): void {
		this.navigationService.navigateToReviewKyc();
	}

	public closeModal(): void {
		this.universalModal.close(ModalCloseReason.close);
	}

	public resetErrorEvent(): void {
		this.changeEmailData$$.next({ ...this.changeEmailData$$.value, errorSmsPin: false });
	}

	public resetBehaviorSubject(): void {
		this.changeEmailData$$.next(initialChangeEmailData);
	}

	public rudderstackTracker(model: ChangeInfoTrackingModel): void {
		this.segmentService.track({
			name: model.name,
			properties: {
				screen_it_happened_on: model.location
			}
		});
		this.rudderstackService.track({
			name: model.name,
			properties: {
				screen_it_happened_on: model.location
			}
		});
	}
}
